import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import ProductionUnitApi from "../../services/ProductionUnitApi";
import PlantApi from "../../services/PlantApi";
import { useToast } from "../../context/ToastContext";
import IApiProductionUnitResponse from "../../interfaces/IApiProductionUnitResponse";

// Design System
import FormCard from "../../components/FormCard";
import Input from "../../components/Input/Input";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";

const EditFormProductionUnit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { displayToast } = useToast();
  const { t: translateForm } = useTranslation("form");
  const { t: translateMessages } = useTranslation("messages");
  const { t: translateGeneral } = useTranslation("general");

  const [name, setName] = useState("");
  const [plantId, setPlantId] = useState("");
  const [plants, setPlants] = useState<{ label: string; value: string }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const plantsResponse = await PlantApi.GetAllPlants();
      const options = (plantsResponse.data || []).map((p: any) => ({
        label: p.name,
        value: p.id,
      }));
      setPlants(options.sort((a: any, b: any) => a.label.localeCompare(b.label)));

      const unitsResponse = await ProductionUnitApi.GetAllProductUnit();
      const unit = (unitsResponse.data || []).find((u: IApiProductionUnitResponse) => u.id === id);

      if (unit) {
        setName(unit.name);
        setPlantId(unit.plantId);
      } else {
        displayToast("error", translateForm("error"), translateGeneral("noRecordsFound"), false, 3000);
        navigate("/production-units");
      }
    } catch (error: any) {
      displayToast("error", translateForm("error"), error.message || error, false, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async (): Promise<void> => {
    if (!name || !plantId || !id) {
      displayToast("error", translateForm("error"), translateMessages("fillAllFields"), false, 3000);
      return;
    }

    setIsLoading(true);
    try {
      await ProductionUnitApi.UpdateProductUnit(id, plantId, name);
      displayToast("success", translateMessages("editSuccess"), "", false, 3000);
      navigate("/production-units");
    } catch (error: any) {
      displayToast("error", translateForm("error"), error.message || error, false, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormCard
      title={translateMessages("editProductionUnit")}
      icon="edit"
      onSubmit={handleUpdate}
      onCancel={() => setShowConfirmCancel(true)}
      submitButtonLabel={translateGeneral("save")}
      cancelButtonLabel={translateGeneral("cancel")}
      disabled={isLoading}
      numColumns={6}
    >
      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="text"
          label={translateForm("name")}
          required
          value={name}
          onChange={(val: any) => setName(val)}
        />
      </div>
      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="select"
          label={translateForm("plant")}
          required
          value={plantId}
          options={plants}
          optionLabel="label"
          optionValue="value"
          onChange={(val: any) => setPlantId(val)}
        />
      </div>

      <ConfirmModal
        visible={showConfirmCancel}
        onHide={() => setShowConfirmCancel(false)}
        onConfirm={() => navigate("/production-units")}
      />
    </FormCard>
  );
};

export default EditFormProductionUnit;
